<template>
  <div>
    <b-alert
      v-if="errorText"
      show
      variant="danger"
    >
      <div class="alert-body">
        {{ errorText }}
      </div>
    </b-alert>

    <div
      v-if="loading"
      class="text-center py-2"
    >
      <b-spinner />
    </div>

    <template v-else-if="catalog">
      <b-card class="mb-1">
        <b-alert
          v-if="readOnly"
          show
          variant="secondary"
        >
          <div class="alert-body">
            Системная роль: её права задаются в коде и не меняются здесь.
            <template v-if="form.key === 'superadmin'">
              Суперадмин проходит любую проверку, включая системные настройки.
            </template>
          </div>
        </b-alert>
        <b-row>
          <b-col md="5">
            <b-form-group label="Название">
              <b-form-input
                v-model="form.name"
                :disabled="readOnly"
                placeholder="Например, «Менеджер магазина»"
              />
            </b-form-group>
          </b-col>
          <b-col md="7">
            <b-form-group label="Описание">
              <b-form-input
                v-model="form.description"
                :disabled="readOnly"
                placeholder="Для кого роль и что она даёт"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <div class="d-flex flex-wrap align-items-center">
          <b-form-input
            v-model="search"
            placeholder="Поиск по правам"
            class="mr-1 mb-50 search"
          />
          <span class="text-muted small mb-50 mr-1">
            Выбрано прав: {{ selected.length }}.
            <b-badge variant="light-info">ⓢ</b-badge> — действует только на старты, выбранные при назначении роли;
            <b-badge variant="light-danger">опасное</b-badge> — деньги, личные данные, система.
          </span>
          <b-button
            v-if="!readOnly"
            variant="primary"
            class="ml-auto mb-50"
            :disabled="saving || !form.name.trim()"
            @click="save"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-50"
            />
            Сохранить
          </b-button>
        </div>
      </b-card>

      <b-row>
        <b-col
          v-for="group in visibleGroups"
          :key="group.group"
          lg="6"
        >
          <b-card
            no-body
            class="mb-1"
          >
            <b-card-header class="py-1">
              <b-form-checkbox
                :checked="groupState(group) === 'all'"
                :indeterminate="groupState(group) === 'some'"
                :disabled="readOnly || !group.permissions.every(canChange)"
                @change="toggleGroup(group, $event)"
              >
                <strong>{{ group.group }}</strong>
              </b-form-checkbox>
            </b-card-header>
            <b-card-body class="pt-0">
              <div
                v-for="p in group.permissions"
                :key="p.key"
                class="permission py-25"
              >
                <b-form-checkbox
                  :checked="has(p.key)"
                  :disabled="readOnly || !canChange(p)"
                  @change="toggle(p.key, $event)"
                >
                  {{ p.title }}
                  <b-badge
                    v-if="p.startScoped"
                    v-b-tooltip.hover
                    title="Только на старты, выбранные при назначении роли"
                    variant="light-info"
                    class="ml-25"
                  >ⓢ</b-badge>
                  <b-badge
                    v-if="p.danger"
                    variant="light-danger"
                    class="ml-25"
                  >опасное</b-badge>
                </b-form-checkbox>
                <div class="small text-muted pl-2">
                  <code>{{ p.key }}</code>
                  <span v-if="p.description"> — {{ p.description }}</span>
                  <span v-if="!readOnly && !canChange(p)"> — этого права нет у вас самих</span>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BCardBody, BCardHeader, BCol, BFormCheckbox, BFormGroup, BFormInput, BRow, BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import aclApi from '@/services/acl-api.service'
import {
  byKey, dependentsOf, groupsOf, loadCatalog, withImplied,
} from './catalog'

/**
 * One role: name, description and the permission matrix by catalog group. Ticking a permission ticks what it implies,
 * unticking it unticks what depends on it. A permission the editor does not have is locked (the API refuses it too).
 */
export default {
  name: 'AclRolePage',
  components: {
    BAlert, BBadge, BButton, BCard, BCardBody, BCardHeader, BCol, BFormCheckbox, BFormGroup, BFormInput, BRow, BSpinner,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      catalog: null,
      form: {
        key: '', name: '', description: '', isSystem: false,
      },
      selected: [],
      initial: [],
      search: '',
      loading: false,
      saving: false,
      errorText: '',
    }
  },
  computed: {
    isNew() {
      return this.$route.params.id === 'new'
    },
    readOnly() {
      return this.form.isSystem
    },
    groups() {
      return this.catalog ? groupsOf(this.catalog) : []
    },
    visibleGroups() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.groups
      return this.groups
        .map(g => ({
          ...g,
          permissions: g.permissions.filter(p => `${g.group} ${p.title} ${p.key} ${p.description || ''}`.toLowerCase().includes(q)),
        }))
        .filter(g => g.permissions.length)
    },
  },
  watch: {
    '$route.params.id': 'load',
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.errorText = ''
      try {
        this.catalog = await loadCatalog()
        const sourceId = this.isNew ? this.$route.query.from : this.$route.params.id
        if (sourceId) {
          const role = (await aclApi.role(sourceId)).data
          this.form = {
            key: this.isNew ? '' : role.key,
            name: this.isNew ? `${role.name} (копия)` : role.name,
            description: role.description || '',
            isSystem: !this.isNew && role.isSystem,
          }
          this.selected = role.key === 'superadmin'
            ? this.catalog.permissions.filter(p => !p.superadminOnly).map(p => p.key)
            : role.permissions.filter(key => byKey(this.catalog)[key] && !byKey(this.catalog)[key].superadminOnly)
        } else {
          this.form = {
            key: '', name: '', description: '', isSystem: false,
          }
          this.selected = []
        }
        this.initial = this.isNew ? [] : [...this.selected]
      } catch (e) {
        this.errorText = e?.response?.data?.message || 'Не удалось загрузить роль'
      } finally {
        this.loading = false
      }
    },
    has(key) {
      return this.selected.includes(key)
    },
    /** The editor may add or remove only permissions they have themselves */
    canChange(p) {
      return this.$perm(p.key)
    },
    toggle(key, on) {
      const next = new Set(this.selected)
      if (on) {
        withImplied(this.catalog, [key]).forEach(k => next.add(k))
      } else {
        next.delete(key)
        dependentsOf(this.catalog, key).forEach(k => next.delete(k))
      }
      this.selected = [...next].filter(k => !byKey(this.catalog)[k].superadminOnly)
    },
    groupState(group) {
      const count = group.permissions.filter(p => this.has(p.key)).length
      if (!count) return 'none'
      return count === group.permissions.length ? 'all' : 'some'
    },
    toggleGroup(group, on) {
      group.permissions.forEach(p => this.toggle(p.key, on))
    },
    async save() {
      const added = this.selected.filter(k => !this.initial.includes(k))
      const dangerous = added.map(k => byKey(this.catalog)[k]).filter(p => p.danger)
      if (dangerous.length) {
        const ok = await this.$bvModal.msgBoxConfirm(
          `Роль получит опасные права: ${dangerous.map(p => p.title).join(', ')}. Сохранить?`,
          {
            title: 'Опасные права', okVariant: 'danger', okTitle: 'Сохранить', cancelTitle: 'Отмена', centered: true,
          },
        )
        if (!ok) return
      }

      this.saving = true
      try {
        const body = { name: this.form.name.trim(), description: this.form.description.trim() || null, permissions: this.selected }
        const role = this.isNew
          ? (await aclApi.createRole(body)).data
          : (await aclApi.updateRole(this.$route.params.id, body)).data
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Роль сохранена', icon: 'CheckIcon', variant: 'success', text: 'Права действуют с ближайшего запроса',
          },
        })
        if (this.isNew) {
          this.$router.replace({ name: 'acl-role', params: { id: role.id } })
        } else {
          this.initial = [...this.selected]
        }
      } catch (e) {
        // the global toast already shows the reason
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.search {
  max-width: 320px;
}
.permission + .permission {
  border-top: 1px solid rgba(34, 41, 47, 0.06);
}
</style>
